import type { CzescTresci, StanTresciDto, TrescOknoDto } from "@shared/wywolaniaTypy.js";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { etykietaZrodlaTresci, komunikatBleduApi, liczbaZOdmiana, opisBrakuTresci } from "../../lib/developer.js";
import { formatujLiczbe } from "../../lib/koszty.js";
import { IconKopiuj } from "../icons/index.js";
import { Badge } from "../ui/Badge.js";
import { Button } from "../ui/Button.js";
import { Card } from "../ui/Card.js";

/**
 * Karta z trescia zapytania albo odpowiedzi jednego wywolania.
 *
 * Tresc to czesto czyjas rozmowa (polecenie podzialu ma do 400 tys. znakow), wiec jest ZWINIETA: do czasu klikniecia
 * "Pokaz tresc" nie jest nawet pobierana, a przy pokazaniu stoi ostrzezenie. Serwer oddaje ja oknami (po ok. 20 tys.
 * znakow), a "Pokaz dalej" dokleja kolejne. Serwer zapisuje w logu samo id wywolania i nazwe czesci, nigdy tresc.
 *
 * Gdy tresci nie ma, mowimy DLACZEGO (wygasla zgodnie z polityka 30 dni, nigdy niezapisana, wywolanie w toku) i pokazujemy
 * to, co zostaje: sume kontrolna i rozmiar. Monospace dla tresci to jedyny wyjatek od TY-3 obok KPI (WY-5, przez analogie).
 *
 * Dwa zabezpieczenia stanu: kazde pobranie ma numer i odpowiedz inna niz ostatnio zlecona jest pomijana (spozniona odpowiedz
 * "Pokaz dalej" po "Ukryj" albo po zmianie wywolania nie wskrzesza tresci od srodka), a fokus nie ginie, gdy przycisk, ktory go
 * mial, znika z DOM (A-4): po "Pokaz tresc" idzie na blok z trescia, po ostatnim "Pokaz dalej" na "Kopiuj", po "Ukryj" na
 * "Pokaz tresc".
 */

const OKNO_ZNAKOW = 20_000;

type CelFokusu = "blok" | "kopiuj" | "pokaz";

export function TrescWywolania({
  id,
  czesc,
  tytul,
  stan,
}: {
  id: string;
  czesc: CzescTresci;
  tytul: string;
  stan: StanTresciDto;
}): React.JSX.Element {
  const [tekst, setTekst] = useState<string | null>(null);
  const [okno, setOkno] = useState<TrescOknoDto | null>(null);
  const [ladowanie, setLadowanie] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);
  const [skopiowano, setSkopiowano] = useState(false);
  const zegarRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const numerRef = useRef(0);
  const fokusRef = useRef<CelFokusu | null>(null);
  const blokRef = useRef<HTMLPreElement>(null);
  const kopiujRef = useRef<HTMLButtonElement>(null);
  const pokazRef = useRef<HTMLButtonElement>(null);
  const kluczRef = useRef(`${id}/${czesc}`);

  // Inne wywolanie albo inna czesc to inna tresc: nic z poprzedniej nie moze zostac na ekranie, a pobranie w locie traci wage.
  // Tylko przy ZMIANIE, nie przy zamontowaniu. Do 03.11.2026 efekt biegl tez przy zamontowaniu, jako zwykly useEffect, czyli
  // po narysowaniu karty. Klik w "Pokaz tresc", ktory go wyprzedzil (szybki klik, obciazona maszyna), dostawal numer, ktory
  // efekt zaraz podbijal, wiec odpowiedz na ten klik szla do kosza, a przycisk wygladal na martwy. useLayoutEffect biegnie
  // jeszcze przy zatwierdzaniu renderu, wiec zaden klik go nie wyprzedzi, a stara tresc nie mignie pod nowym wywolaniem.
  useLayoutEffect(() => {
    const klucz = `${id}/${czesc}`;
    if (klucz === kluczRef.current) return;
    kluczRef.current = klucz;
    numerRef.current += 1;
    setTekst(null);
    setOkno(null);
    setBlad(null);
    setLadowanie(false);
  }, [id, czesc]);

  // Przy odmontowaniu tylko zegar napisu "Skopiowano". Numeru pobrania tu nie podbijamy: spozniona odpowiedz po
  // odmontowaniu i tak niczego nie zmieni, a w StrictMode to sprzatanie biegnie tez przy samym zamontowaniu i wyrzucaloby
  // odpowiedz na wczesny klik.
  useEffect(
    () => () => {
      if (zegarRef.current) clearTimeout(zegarRef.current);
    },
    [],
  );

  // Przeniesienie fokusu PO renderze, w ktorym element docelowy juz istnieje.
  useEffect(() => {
    const cel = fokusRef.current;
    if (cel === null) return;
    const el = cel === "blok" ? blokRef.current : cel === "kopiuj" ? kopiujRef.current : pokazRef.current;
    if (el) {
      el.focus();
      fokusRef.current = null;
    }
  }, [tekst, okno]);

  async function pobierz(offset: number): Promise<void> {
    const numer = ++numerRef.current;
    setLadowanie(true);
    setBlad(null);
    try {
      const odp = await api.developerTresc(id, czesc, offset, OKNO_ZNAKOW);
      if (numer !== numerRef.current) return;
      if (!odp.okno) {
        setBlad("Serwer nie zwrócił treści (stan mógł się zmienić). Odśwież stronę.");
        return;
      }
      const nowe = odp.okno;
      setTekst((poprzedni) => (offset === 0 || poprzedni === null ? nowe.tekst : poprzedni + nowe.tekst));
      setOkno(nowe);
      // Pierwsze okno: fokus na blok z trescia. Kolejne: gdy "Pokaz dalej" wlasnie zniknal (koniec tresci), na "Kopiuj".
      fokusRef.current = offset === 0 ? "blok" : nowe.jestDalej ? null : "kopiuj";
    } catch (err) {
      if (numer !== numerRef.current) return;
      setBlad(`Nie udało się pobrać treści. ${komunikatBleduApi(err instanceof ApiError ? err.code : "siec")}`);
    } finally {
      if (numer === numerRef.current) setLadowanie(false);
    }
  }

  async function kopiuj(): Promise<void> {
    if (tekst === null) return;
    try {
      await navigator.clipboard.writeText(tekst);
      setSkopiowano(true);
      if (zegarRef.current) clearTimeout(zegarRef.current);
      zegarRef.current = setTimeout(() => setSkopiowano(false), 2000);
    } catch {
      setBlad("Przeglądarka nie pozwoliła skopiować. Zaznacz tekst ręcznie.");
    }
  }

  function ukryj(): void {
    // Pobranie w locie traci wage: spozniona odpowiedz nie moze odslonic tresci od srodka.
    numerRef.current += 1;
    setLadowanie(false);
    setTekst(null);
    setOkno(null);
    fokusRef.current = "pokaz";
  }

  const brak = opisBrakuTresci(stan);
  const sumaIRozmiar =
    stan.stan === "wygasla" || stan.stan === "zapisana"
      ? [stan.sha256 ? `SHA-256: ${stan.sha256}` : null, stan.bajty !== null ? `${formatujLiczbe(stan.bajty)} B` : null]
          .filter(Boolean)
          .join(" - ")
      : "";

  return (
    <Card tytul={tytul} akcje={stan.stan === "zapisana" && stan.ucieta ? <Badge wariant="warning">ucięta przy zapisie</Badge> : undefined}>
      {brak !== null && (
        <p className="developer-brak" data-stan={stan.stan}>
          {brak}
        </p>
      )}
      {brak !== null && sumaIRozmiar !== "" && <p className="developer-meta">{sumaIRozmiar}</p>}

      {stan.stan === "zapisana" && (
        <>
          <p className="developer-meta">
            {liczbaZOdmiana(stan.znaki, "znak", "znaki", "znaków")}, {etykietaZrodlaTresci(stan.zrodlo)}
            {sumaIRozmiar !== "" ? `. ${sumaIRozmiar}` : ""}
          </p>

          {tekst === null ? (
            <>
              <p className="developer-ostrzezenie">
                Treść może zawierać fragmenty rozmów i dane osobowe. Otwarcie zostawia w logu serwera ślad (samo id wywołania).
              </p>
              <Button ref={pokazRef} zajety={ladowanie} onClick={() => void pobierz(0)}>
                Pokaż treść
              </Button>
            </>
          ) : (
            <>
              <pre ref={blokRef} className="blok-tresci" tabIndex={0} aria-label={`${tytul}: treść wywołania`}>
                {tekst}
              </pre>
              <div className="blok-tresci__pasek">
                <span className="developer-meta">
                  Wyświetlono {formatujLiczbe(tekst.length)} z {formatujLiczbe(okno?.razem ?? tekst.length)} znaków
                </span>
                <div className="blok-tresci__akcje">
                  {okno?.jestDalej && (
                    <Button zajety={ladowanie} onClick={() => void pobierz(okno.offset + okno.dlugosc)}>
                      Pokaż dalej
                    </Button>
                  )}
                  <Button ref={kopiujRef} onClick={() => void kopiuj()}>
                    <IconKopiuj size={18} />
                    {skopiowano ? "Skopiowano" : okno?.jestDalej ? "Kopiuj wyświetlone" : "Kopiuj"}
                  </Button>
                  <Button onClick={ukryj}>Ukryj</Button>
                </div>
              </div>
            </>
          )}
          {blad && (
            <p className="developer-blad" role="alert">
              {blad}
            </p>
          )}
        </>
      )}
    </Card>
  );
}
